import { Component, DoCheck, EventEmitter, Inject, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { EmployeeDetails, EmployeeDetailsPost, StiiraError } from '@core/models';
import { AvatarService, EmployeeFormService, ErrorService, LayoutService, SnackbarService } from '@core/services';
import { ProfileService } from '@core/services/profile.service';
import { indicate } from '@shared/helpers/rxjs.helpers';
import { BehaviorSubject, noop, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { closeDialogOnEscape } from '@shared/helpers';

@Component({
    selector: 'app-edit-employee',
    templateUrl: './edit-employee.component.html',
    styleUrls: ['./edit-employee.component.scss'],
    providers: [EmployeeFormService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditEmployeeComponent implements OnInit, DoCheck, OnDestroy {
  static readonly componentName = 'EditEmployeeComponent';
  
  @Output() editEmployeeHasChanges = new EventEmitter<boolean>();

  public isSubmitting$: Observable<boolean>;

  public position = position;
  public onDragEnded = onDragEnded;

  private formChangeEmitted = false;
  private destroy$: Subject<void> = new Subject<void>();
  private isSubmittingSubject = new BehaviorSubject<boolean>(false);

  get eeForm(): FormGroup {
    return this.eeFormService.form;
  } 
  
  get noChanges(): boolean {
    return JSON.stringify(this.eeForm.value) === JSON.stringify(this.eeFormService.formInitValues);
  }

  public isMobile$: Observable<boolean>;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { eeData: EmployeeDetails; sysText: any },
    private profileService: ProfileService,
    private eeFormService: EmployeeFormService,
    private layoutService: LayoutService,
    private editEmployeeDialogRef: MatDialogRef<EditEmployeeComponent>,
    private dialog: MatDialog,
    private errorService: ErrorService,
    private snackbarService: SnackbarService,
    private avatarService: AvatarService
  ) {
    this.isSubmitting$ = this.isSubmittingSubject.asObservable();
  }

  ngOnInit() {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.editEmployeeDialogRef, this.isSubmitting$, () => this.close());

    this.eeFormService.loadFormForEdit(this.data.eeData);

    this.editEmployeeDialogRef
      .backdropClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => (this.isSubmittingSubject.value ? noop : this.close()));
  }

  ngDoCheck() {
    if (!this.formChangeEmitted) {
      if (!this.noChanges) 
      {
        this.editEmployeeHasChanges.emit(true);
        this.formChangeEmitted = true;
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }  

  public save(): void {
    const details: EmployeeDetailsPost = this.eeFormService.createEmployeeDetailsDto();
    this.profileService
      .updateEmployeeDetails(details)
      .pipe(
        indicate(this.isSubmittingSubject)
      )
      .subscribe(
        (res) => (
          this.profileService.updateCachedEmployeeDetails(res),
          this.avatarService.updateName(res.firstName + " " + res.lastName),
          this.snackbarService.open('Employee details updated', 'Dismiss'),
          this.editEmployeeDialogRef.close(details)
        ),
        (err: StiiraError) => this.errorService.setFormModelStateErrors(this.eeForm, err.modelStateErrors)
      );
  }  

  close(): void {
    if (this.noChanges) {
      this.editEmployeeDialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.sysText.unsavedChanges
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.editEmployeeDialogRef.close();
        }
      });
  }
}
